import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Moon, BookOpen, Award, ChevronRight, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { useLanguage } from '../contexts/LanguageContext';
import { 
  isOnboardingCompleted, 
  completeOnboarding, 
  getRecommendedTier 
} from '../utils/conversionTracking';

/**
 * Simple Onboarding Modal - Tier Recommendation
 * Non-intrusive, professional approach
 */
const OnboardingModal = ({ onComplete }) => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [visible, setVisible] = useState(false);
  const [selectedType, setSelectedType] = useState(null);
  const [showRecommendation, setShowRecommendation] = useState(false);

  useEffect(() => {
    // Show modal if onboarding not completed and user is new
    if (!isOnboardingCompleted()) {
      // Delay to not interrupt initial page load
      const timer = setTimeout(() => setVisible(true), 3000);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleSelect = (type) => {
    setSelectedType(type);
    setShowRecommendation(true);
  };

  const handleComplete = (goToMembership = false) => {
    if (selectedType) {
      completeOnboarding(selectedType);
    }
    setVisible(false);
    if (onComplete) onComplete(selectedType);
    if (goToMembership) {
      navigate('/membership');
    }
  };

  const handleSkip = () => {
    completeOnboarding('skipped');
    setVisible(false);
    if (onComplete) onComplete(null);
  };

  if (!visible) return null;

  const content = {
    en: {
      title: 'What brings you here?',
      subtitle: 'Help us personalize your experience',
      options: [
        { 
          id: 'improve_sleep', 
          icon: Moon, 
          title: 'Improve my sleep',
          description: 'Better habits, better rest'
        },
        { 
          id: 'learn', 
          icon: BookOpen, 
          title: 'Learn about sleep',
          description: 'In-depth sleep education'
        },
        { 
          id: 'certification', 
          icon: Award, 
          title: 'Prepare for exams',
          description: 'Professional training'
        },
      ],
      recommendation: 'Recommended for you',
      viewPlans: 'View Plans',
      continueFree: 'Continue Free',
      skip: 'Skip for now',
    },
    es: {
      title: '¿Qué te trae aquí?',
      subtitle: 'Ayúdanos a personalizar tu experiencia',
      options: [
        { 
          id: 'improve_sleep', 
          icon: Moon, 
          title: 'Mejorar mi sueño',
          description: 'Mejores hábitos, mejor descanso'
        },
        { 
          id: 'learn', 
          icon: BookOpen, 
          title: 'Aprender sobre el sueño',
          description: 'Educación profunda del sueño'
        },
        { 
          id: 'certification', 
          icon: Award, 
          title: 'Preparación para exámenes',
          description: 'Entrenamiento profesional'
        },
      ],
      recommendation: 'Recomendado para ti',
      viewPlans: 'Ver Planes',
      continueFree: 'Continuar Gratis',
      skip: 'Omitir por ahora',
    },
  };

  const t = content[language] || content.en;
  const recommendation = selectedType ? getRecommendedTier(selectedType) : null;

  return (
    <div 
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      data-testid="onboarding-modal"
    >
      <Card className="w-full max-w-md bg-white shadow-xl animate-in fade-in zoom-in duration-300">
        <CardContent className="p-6">
          {!showRecommendation ? (
            <>
              <div className="text-center mb-6">
                <h2 className="text-xl font-bold text-gray-900 mb-1">{t.title}</h2>
                <p className="text-sm text-gray-500">{t.subtitle}</p>
              </div>

              <div className="space-y-3">
                {t.options.map((option) => {
                  const Icon = option.icon;
                  return (
                    <button
                      key={option.id}
                      onClick={() => handleSelect(option.id)}
                      className="w-full flex items-center gap-4 p-4 rounded-lg border border-gray-200 hover:border-blue-300 hover:bg-blue-50 transition-all text-left group"
                      data-testid={`onboarding-option-${option.id}`}
                    >
                      <div className="p-2 bg-gray-100 rounded-lg group-hover:bg-blue-100 transition-colors">
                        <Icon className="h-5 w-5 text-gray-600 group-hover:text-blue-600" />
                      </div>
                      <div className="flex-1">
                        <p className="font-medium text-gray-900">{option.title}</p>
                        <p className="text-sm text-gray-500">{option.description}</p>
                      </div>
                      <ChevronRight className="h-5 w-5 text-gray-400 group-hover:text-blue-500" />
                    </button>
                  );
                })}
              </div>

              <button
                onClick={handleSkip}
                className="w-full text-center text-sm text-gray-500 hover:text-gray-700 mt-4 py-2"
                data-testid="onboarding-skip"
              >
                {t.skip}
              </button>
            </>
          ) : (
            <>
              <div className="text-center mb-6">
                <div className="inline-flex items-center gap-1 text-emerald-600 text-sm font-medium mb-2">
                  <Check className="h-4 w-4" />
                  {t.recommendation}
                </div>
                <h2 className="text-2xl font-bold text-gray-900 mb-1">
                  {recommendation?.name}
                </h2>
                <p className="text-gray-600">{recommendation?.reason}</p>
              </div>

              <div className="space-y-3">
                <Button
                  onClick={() => handleComplete(true)}
                  className="w-full bg-blue-600 hover:bg-blue-700"
                  data-testid="onboarding-view-plans"
                >
                  {t.viewPlans}
                  <ChevronRight className="h-4 w-4 ml-1" />
                </Button>
                <Button
                  variant="outline"
                  onClick={() => handleComplete(false)}
                  className="w-full"
                  data-testid="onboarding-continue-free"
                >
                  {t.continueFree}
                </Button>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OnboardingModal;
